import { makeStyles, Theme } from '@material-ui/core';
import { Tab, Tabs, Typography, Tooltip } from '@thingsw/pitta-design-system';
import clsx from 'clsx';
import _ from 'lodash';
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { RootState } from '../../features/store';
import { USER } from '../../features/User/slice';
import { ScreenDefaultProps } from '../../hoc/withViewerTemplate';
import { openToast } from '../../features/Toast/slice';
import {
  ICameraInfo,
  SemanticDarkColors,
  Webviewer,
  PSN650,
  AvailableFw,
  PermissionProps,
} from '@thingsw/pitta-modules';
import { FleetaCameraMenu } from '../fleeta/FleetaCameraMenu';
import useRole from '../../hooks/useRole';
import { loadCameras, setCameraTimeSync } from '../../features/Camera/slice';
import UnpairBatteryModal from '../modals/UnpairBatteryModal';
import { EditProfileModal } from '../uxui/EditProfileModal';
import { getPatialUnsupportModel } from '../../utils/getPatialUnsupportModel';

interface PlaybackHeaderProps {
  openMenu: boolean;
  currentCam?: ICameraInfo;
  mobile: boolean;
  value: number;
  onRename?: (camera: ICameraInfo) => void;
  onDelete?: (camera: ICameraInfo) => void;
  onUpdate?: (camera: ICameraInfo) => void;
  onRebootCamera?: (camera: ICameraInfo) => void;
  setNotificationCam: (camera: ICameraInfo) => void;
  setOpenNotification: (open: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  tabDiv: {
    backgroundColor: (props: ScreenDefaultProps) =>
      props.color === 'dark'
        ? SemanticDarkColors.primary['1']
        : props.colors.primary['0'],
    top: (props: ScreenDefaultProps) => props.headerDivRef?.offsetHeight ?? 56,
    ...(theme.direction === 'rtl'
      ? { left: 0, right: 0 }
      : { left: 235, right: 0 }),
    position: 'fixed',
    zIndex: 90,
    height: 53 + 29,
    display: 'flex',
    alignItems: 'flex-end',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      padding: theme.spacing(0, 4),
      height: 53,
      ...(theme.direction === 'rtl'
        ? { left: 0, right: 235 }
        : { left: 235, right: 0 }),
    },
  },
  tabDivClosed: {
    left: 73,
  },
  tabDivMobile: {
    left: 0,
    flexDirection: 'column',
    alignItems: 'unset',
  },
  mobileCamNameDiv: {
    display: 'flex',
    paddingTop: theme.spacing(1),
    justifyContent: 'center',
  },
  tabScroll: {
    borderBottom: (props: ScreenDefaultProps) =>
      `1px solid ${props.colors.primary['5']}`,
    overflowX: 'scroll',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    [theme.breakpoints.up(Webviewer.mobile)]: {
      overflowX: 'unset',
      width: '100%',
    },
  },
  tab: {
    display: 'flex',
    // justifyContent: "space-between",
    minWidth: 570,
    width: '100%',
    height: 52,
    alignItems: 'flex-end',
    [theme.breakpoints.down(Webviewer.mobile)]: {
      minWidth: 'auto',
    },
  },
  tabItem: {
    display: 'flex',
    alignItems: 'flex-end',
    paddingBottom: 6,
    whiteSpace: 'nowrap',
    color: (props: ScreenDefaultProps) => props.colors.primary['1'],
  },
}));
export const PlaybackHeader = (
  props: PlaybackHeaderProps & ScreenDefaultProps & PermissionProps
) => {
  const {
    openMenu,
    currentCam,
    mobile,
    value,
    onDelete,
    onRebootCamera,
  } = props;
  const {
    gpsTrackingPerm,
    reportPerm,
    settingsCamPerm,
    fotaCamPerm,
    deleteCamPerm,
    liveviewPerm,
    rebootCamPerm,
    setNotificationCam,
    setOpenNotification,
  } = props;
  const classes = useStyles(props);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const tabRefs = useRef<(HTMLElement | null)[]>([]);
  const tabScrollRef = useRef<HTMLDivElement>(null);

  const match = useRouteMatch<{ psn: string }>('/cameras/:psn');
  const history = useHistory();

  const userProfile = useSelector(
    (state: RootState) => state[USER].userProfile
  );

  const [openLiveviewTooltip, setOpenLiveviewTooltip] = useState(false);
  const [openGpsTooltip, setOpenGpsTooltip] = useState(false);
  const [openReportTooltip, setOpenReportTooltip] = useState(false);
  const [openSettingsTooltip, setOpenSettingsTooltip] = useState(false);
  // const [openMoreTabTooltip, setOpenMoreTabTooltip] = useState(false);
  const [openCamMenu, setOpenCamMenu] = useState(false);
  const [openEditProfile, setOpenEditProfile] = useState(false);

  const [openUnpairBattery, setOpenUnpairBattery] = useState(false);
  const [unpairBatteryCam, setUnpairBatteryCam] = useState<ICameraInfo>();

  const { isOwner } = useRole();

  // mantis : 8831 - KimSeongHyun
  // 탭 이동시 스크롤 최상위로 이동
  const scrollToTop = useCallback(() => {
    document.getElementById('scrollTop')?.scrollIntoView(true);
  }, []);

  // value 값에 따라 선택된 탭으로 스크롤
  useEffect(() => {
    if (mobile && tabRefs.current[value] && tabScrollRef.current) {
      const tabElement = tabRefs.current[value];
      const scrollContainer = tabScrollRef.current;
      
      if (tabElement) {
        const tabRect = tabElement.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        
        // 탭이 컨테이너 내에서 보이는지 확인
        const isVisible = 
          tabRect.left >= containerRect.left && 
          tabRect.right <= containerRect.right;
        
        // 탭이 화면에 보이지 않을 때만 스크롤
        if (!isVisible) {
          // 스크롤 컨테이너 내에서의 상대적 위치 계산
          const tabOffsetLeft = tabElement.offsetLeft;
          const containerOffsetLeft = scrollContainer.offsetLeft;
          
          // 선택된 탭을 왼쪽으로 스크롤
          const scrollOffset = tabOffsetLeft - containerOffsetLeft;
          let targetScrollLeft = scrollContainer.scrollLeft + scrollOffset;            
          
          // 스크롤 실행
          scrollContainer.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth'
          });
        }
      }
    }
  }, [value, mobile]);

  const handleLiveTooltipHover = useCallback(() => {
    if (!liveviewPerm) {
      setOpenLiveviewTooltip(true);
    }
  }, [liveviewPerm]);

  // const handleMoreTabTooltipHover = useCallback(() => {
  //   if (!moreTabPerm) {
  //     setOpenMoreTabTooltip(true);
  //   } else {
  //     return openMoreTabTooltip;
  //   }
  // }, [moreTabPerm, openMoreTabTooltip]);

  const handleGpsTooltipHover = useCallback(() => {
    if (!gpsTrackingPerm && !mobile) {
      setOpenGpsTooltip(true);
    }
  }, [gpsTrackingPerm, mobile]);

  const handleReportTooltipHover = useCallback(() => {
    if (!reportPerm) {
      setOpenReportTooltip(true);
    }
  }, [reportPerm]);

  const handleSettingsTooltipHover = useCallback(() => {
    if (!settingsCamPerm) {
      setOpenSettingsTooltip(true);
    }
  }, [settingsCamPerm]);

  const handleRebbotCam = useMemo(() => {
    return rebootCamPerm
      ? () => {
          currentCam && onRebootCamera?.(currentCam);
          setOpenCamMenu(false);
        }
      : null;
  }, [currentCam, onRebootCamera, rebootCamPerm]);

  const handleSettings: (() => void) | null = useMemo(() => {
    return settingsCamPerm
      ? () => {
          console.log('moreCam', currentCam);
          if (currentCam) {
            if (currentCam.require_time_sync)
              dispatch(setCameraTimeSync(currentCam.require_time_sync));
            history.push(`/cameras/${currentCam.psn}/settings`);
          }
          setOpenCamMenu(false);
        }
      : null;
  }, [history, currentCam, settingsCamPerm, dispatch]);

  const handleUnpairBattery = (camera: ICameraInfo) => {
    setOpenUnpairBattery(true);
    setUnpairBatteryCam(camera);
    setOpenCamMenu(false);
  };

  const handleFOTA: (() => void) | null = useMemo(() => {
    return fotaCamPerm && !_.includes(PSN650, currentCam?.psn.substr(0, 4))
      ? (fw?: AvailableFw) => {
          if (fw) {
            if (currentCam && currentCam.active === 'on') {
              if (fw.dmc200) {
                history.push(`/cameras/${currentCam.psn}/fota-dmc200`);
              } else if (fw.blackvue) {
                history.push(`/cameras/${currentCam.psn}/fota`);
              }
            }
          } else {
            dispatch(openToast({ message: 'Firmware is up_' }));
            setOpenCamMenu(false);
          }
        }
      : null;
  }, [currentCam, dispatch, fotaCamPerm, history]);

  const handleDelete = useMemo(() => {
    return deleteCamPerm
      ? () => {
          setOpenCamMenu(false);
          currentCam && onDelete?.(currentCam);
        }
      : null;
  }, [currentCam, deleteCamPerm, onDelete]);

  return (
    <div
      className={clsx(classes.tabDiv, {
        [classes.tabDivClosed]: !openMenu,
        [classes.tabDivMobile]: mobile,
      })}
    >
      {mobile && (
        <div className={classes.mobileCamNameDiv}>
          <Typography
            category='Default'
            variant='SmallBold'
            htmlColor={props.colors.primary['2']}
          >
            {currentCam?.dev_name}
          </Typography>
        </div>
      )}

      <div className={classes.tabScroll} ref={tabScrollRef}>
        <div className={clsx(classes.tab)}>
          <Tabs overFlowX='auto' value={value}>
            <Tab
              ref={(el) => (tabRefs.current[0] = el)}
              className={classes.tabItem}
              disabled={
                currentCam?.active === 'off' ||
                getPatialUnsupportModel(currentCam)
              }
              label={t('Remote Playback')}
              onClick={() => {
                history.push(`/cameras/${match?.params.psn}/playback`);
                // mantis :  8831 - KimSeongHyun
                // 탭 이동시 스크롤 최상위로 이동
                scrollToTop();
              }}
            />

            <Tab
              ref={(el) => (tabRefs.current[1] = el)}
              className={classes.tabItem}
              label={
                <Tooltip
                  open={openLiveviewTooltip}
                  title={t('No permission') ?? 'No permission'}
                  placement='bottom'
                  PopperProps={{
                    modifiers: {
                      offset: {
                        enabled: true,
                        offset: '0, -12px',
                      },
                      flip: {
                        enabled: false,
                      },
                    },
                  }}
                >
                  <div
                    onMouseOver={handleLiveTooltipHover}
                    onMouseOut={() => {
                      setOpenLiveviewTooltip(false);
                    }}
                  >
                    {t('Remote Live view')}
                  </div>
                </Tooltip>
              }
              disabled={currentCam?.active === 'off' || !liveviewPerm}
              onClick={() => {
                history.push(`/cameras/${match?.params.psn}/live-view`, {
                  clicked: true,
                });
                // mantis :  8831 - KimSeongHyun
                // 탭 이동시 스크롤 최상위로 이동
                scrollToTop();
              }}
            />

            <Tab
              ref={(el) => (tabRefs.current[2] = el)}
              className={classes.tabItem}
              label={t('Notifications')}
              onClick={() => {
                if (!currentCam) return;
                setNotificationCam(currentCam);
                setOpenNotification(true);
              }}
            />

            <Tab
              ref={(el) => (tabRefs.current[3] = el)}
              className={classes.tabItem}
              disabled={getPatialUnsupportModel(currentCam)}
              label={t('Cloud Storage')}
              onClick={() => {
                let state;

                if (currentCam?.reg_category === 'cloud') {
                  state = 'live-event-upload';
                }

                history.push(`/cameras/${match?.params.psn}/cloud-storage`, {
                  state,
                });
                // mantis :  8831 - KimSeongHyun
                // 탭 이동시 스크롤 최상위로 이동
                scrollToTop();
              }}
            />
            <Tooltip
              open={openReportTooltip}
              title={t('No permission') ?? 'No permission'}
              placement='bottom'
              PopperProps={{
                modifiers: {
                  offset: {
                    enabled: true,
                    offset: '0, -12px',
                  },
                  flip: {
                    enabled: false,
                  },
                },
              }}
            >
              <div
                onMouseOver={handleReportTooltipHover}
                onMouseOut={() => {
                  setOpenReportTooltip(false);
                }}
              >
                <Tab
                  ref={(el) => (tabRefs.current[4] = el)}
                  className={classes.tabItem}
                  disabled={
                    !reportPerm ||
                    _.includes(PSN650, currentCam?.psn.substr(0, 4))
                  }
                  label={t('Report')}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/report`);
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />
              </div>
            </Tooltip>

            <Tooltip
              open={openGpsTooltip}
              textCenter={
                !(
                  userProfile?.userType === 'User' ||
                  userProfile?.userType === 'SubMaster'
                )
              }
              title={
                userProfile?.userType === 'User' ||
                userProfile?.userType === 'SubMaster' ? (
                  <Typography category='Default' variant='Caption'>
                    {t('No permission')}
                  </Typography>
                ) : (
                  // mantis - 12369, 문구변경 (Leehj)
                  <Typography
                    category='Default'
                    variant='Caption'
                    dangerouslySetInnerHTML={{
                      __html: t('Smart and Fleet_'),
                    }}
                  />
                )
              }
              placement='bottom'
              PopperProps={{
                modifiers: {
                  offset: {
                    enabled: true,
                    offset: '0, -12px',
                  },
                  flip: {
                    enabled: false,
                  },
                },
              }}
            >
              <div
                onMouseOver={handleGpsTooltipHover}
                onMouseOut={() => {
                  setOpenGpsTooltip(false);
                }}
              >
                <Tab
                  ref={(el) => (tabRefs.current[5] = el)}
                  className={classes.tabItem}
                  disabled={!gpsTrackingPerm}
                  label={t('GPS tracking')}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/gps-tracking`);
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />
              </div>
            </Tooltip>

            <Tooltip
              open={openSettingsTooltip}
              title={t('No permission') ?? 'No permission'}
              placement='bottom'
              PopperProps={{
                modifiers: {
                  offset: {
                    enabled: true,
                    offset: '0, -12px',
                  },
                  flip: {
                    enabled: false,
                  },
                },
              }}
            >
              <div
                onMouseOver={handleSettingsTooltipHover}
                onMouseOut={() => {
                  setOpenSettingsTooltip(false);
                }}
              >
                <Tab
                  ref={(el) => (tabRefs.current[6] = el)}
                  className={classes.tabItem}
                  disabled={!settingsCamPerm}
                  label={t('Settings')}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/settings`);
                    scrollToTop();
                  }}
                />
              </div>
            </Tooltip>

            <Tab
              ref={(el) => (tabRefs.current[7] = el)}
              className={classes.tabItem}
              label={t('More')}
              onClick={() => setOpenCamMenu(true)}
            />
          </Tabs>
        </div>
      </div>

      <FleetaCameraMenu
        {...props}
        onClose={() => setOpenCamMenu(false)}
        open={openCamMenu}
        camera={currentCam}
        anchorRef={{ current: tabRefs.current[7] }}
        isHideSettingMenu
        isCloudConnectSettingDisabled
        isFormatSDCardDisabled
        isCameraSettingDisabled={!settingsCamPerm}
        isRemoteFirmwareUpdateDisabled={!fotaCamPerm}
        isRebootCameraDisabled={!isOwner}
        isRemoveCameraDisabled={!isOwner}
        isVisibleConnectDashcamLocally={false}
        placement='top-start'
        onClickAway={() => setOpenCamMenu(false)}
        onClickMenu={(menu) => {
          if (menu === 'CAMERA_PROFILE') {
            setOpenEditProfile(true);
          }

          if (menu === 'CAMERA_SETTING') {
            handleSettings?.();
          }

          if (menu === 'REMOTE_FIRMWARE_UPDATE') {
            handleFOTA?.();
          }

          if (menu === 'REBOOT_CAMERA' && currentCam) {
            handleRebbotCam?.();
          }

          if (menu === 'UNPAIR_BATTERY' && currentCam) {
            handleUnpairBattery(currentCam);
          }

          if (menu === 'DELETE_CAMERA' && currentCam) {
            handleDelete?.();
          }

          setOpenCamMenu(false);
        }}
      />
      <EditProfileModal
        open={openEditProfile}
        camera={currentCam}
        onClose={() => setOpenEditProfile(false)}
        onSuccess={() => {
          setOpenEditProfile(false);
          dispatch(loadCameras());
        }}
      />
      <UnpairBatteryModal
        open={openUnpairBattery}
        camera={unpairBatteryCam}
        onClose={() => setOpenUnpairBattery(false)}
        onSuccess={() => {
          setOpenUnpairBattery(false);
          dispatch(loadCameras());
        }}
      />
    </div>
  );
};
